import type { Locale } from '@/intl/types'
import type { VoucherType } from '@/scopes/payment/domain/vouchers/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { Voucher } from '@/scopes/common/components/Voucher/Voucher'
import { getUsableOwnedVouchers, hasBonusVoucher } from '@/scopes/payment/domain/vouchers/selectors'
import { formatDate } from '@/scopes/payment/formatters'
import styles from './VoucherSelector.module.css'

interface VoucherSelectorProps {
  vouchers: VoucherType[]
  onSelect: (voucher: VoucherType) => void
  onAddVoucherClick: () => void
}

export function VoucherSelector({ vouchers, onSelect, onAddVoucherClick }: VoucherSelectorProps) {
  const intl = useIntl()
  const currentLocale = intl.locale as Locale

  const usableVouchers = getUsableOwnedVouchers(vouchers)
  const showBonus = hasBonusVoucher(usableVouchers)

  return (
    <div className={styles.container}>
      {showBonus && (
        <Banner color="informative" size="S" hasIcon>
          <Banner.TextsWrapper>
            <Banner.Text>{intl.formatMessage({ id: 'payment_scope_voucher_bonus_title' })}</Banner.Text>
            <Banner.SupportingText>{intl.formatMessage({ id: 'payment_scope_voucher_bonus_message' })}</Banner.SupportingText>
          </Banner.TextsWrapper>
        </Banner>
      )}
      <Typography variant="display-h-regular">{intl.formatMessage({ id: 'payment_scope_voucher_available_title' })}</Typography>
      {usableVouchers.length > 0
        ? (
            <div className={styles.grid}>
              {usableVouchers.map(voucher => (
                <Voucher
                  key={voucher.voucherCode}
                  mode="button"
                  amount={voucher.amount}
                  expirationDate={formatDate(voucher.expirationDate, currentLocale)}
                  ariaLabel={intl.formatMessage({ id: 'payment_scope_voucher_to_apply_aria_label' }, { voucherCode: voucher.voucherCode, voucherAmount: intl.formatCurrency(voucher.amount) })}
                  onClick={() => onSelect(voucher)}
                />
              ))}
            </div>
          )
        : (
            <Typography variant="body-m-regular-snug">{intl.formatMessage({ id: 'payment_scope_voucher_none_owned_message' })}</Typography>
          )}
      <div className={styles.actions}>
        <Button type="button" variant="transparentPrimary" onClick={onAddVoucherClick}>
          {intl.formatMessage({ id: 'payment_scope_voucher_add_button' })}
        </Button>
      </div>
    </div>
  )
}
